/* ==========================================================================
   SHOESLI V5 — collection filtering
   Uses Shopify's Section Rendering API. Replaces Dawn's facets.js entirely so
   the filter UI is not bound to Dawn's drawer markup.
   ========================================================================== */
(function () {
  'use strict';

  const cache = new Map();

  class V5Facets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.gridId = 'V5Grid';
      this.countId = 'V5Count';
      this.filtersId = 'V5Filters';

      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPop = this.onPop.bind(this);

      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPop);

      this.setupPopovers();
      this.setupDrawer();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    /* ---------- desktop popovers ---------- */
    setupPopovers() {
      this.querySelectorAll('[data-v5-facet]').forEach((details) => {
        const summary = details.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('click', () => {
          // Only one popover open at a time.
          this.querySelectorAll('[data-v5-facet][open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
      document.addEventListener('click', (e) => {
        if (this.contains(e.target)) return;
        this.querySelectorAll('[data-v5-facet][open]').forEach((d) => (d.open = false));
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = this.querySelector('[data-v5-facet][open]');
        if (!open) return;
        open.open = false;
        const s = open.querySelector('summary');
        if (s) s.focus();
      });
    }

    /* ---------- mobile drawer ---------- */
    setupDrawer() {
      const panel = document.getElementById('V5FilterDrawer');
      const overlay = document.querySelector('[data-v5-filter-overlay]');
      if (!panel || !window.V5Drawer) return;
      this.drawer = new window.V5Drawer(panel, overlay);
      document.querySelectorAll('[data-v5-filter-open]').forEach((b) =>
        b.addEventListener('click', () => this.drawer.open(b))
      );
      panel.addEventListener('click', (e) => {
        if (e.target.closest('[data-v5-filter-close]')) this.drawer.close();
      });

      // Above the bar breakpoint the filters are an inline popover row, not a
      // drawer. Resizing across it must release the scroll lock.
      const desktop = window.matchMedia('(min-width: 990px)');
      const sync = () => { if (desktop.matches && this.drawer.isOpen) this.drawer.close(); };
      desktop.addEventListener('change', sync);
    }

    /* ---------- events ---------- */
    onInput(e) {
      const field = e.target;
      if (!field.matches('input, select')) return;
      if (field.type === 'text' || field.type === 'number') {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.submit(), 600);
      } else {
        this.submit();
      }
    }

    onClick(e) {
      const remove = e.target.closest('[data-v5-facet-remove]');
      if (!remove) return;
      e.preventDefault();
      const href = remove.getAttribute('href') || '';
      const q = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
      this.render(q);
    }

    onPop() {
      this.render(window.location.search.slice(1), false);
    }

    /* ---------- submit ---------- */
    submit() {
      const params = new URLSearchParams();
      this.querySelectorAll('form').forEach((form) => {
        new FormData(form).forEach((value, key) => {
          if (value === '') return;
          params.append(key, value);
        });
      });
      this.render(params.toString());
    }

    async render(search, push = true) {
      const url = `${window.location.pathname}?section_id=${this.sectionId}&${search}`;
      this.setBusy(true);

      let html = cache.get(url);
      if (!html) {
        try {
          const res = await fetch(url);
          html = await res.text();
          cache.set(url, html);
        } catch (err) {
          this.setBusy(false);
          return;
        }
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.swap(doc, this.gridId);
      this.swap(doc, this.countId);
      this.swapFilters(doc);

      if (push) {
        history.pushState(
          { search },
          '',
          `${window.location.pathname}${search ? '?' + search : ''}`
        );
      }
      this.setBusy(false);
      if (window.v5InitReveal) window.v5InitReveal(document.getElementById(this.gridId));

      // Announce the new result count for screen readers.
      const live = document.getElementById('V5FacetStatus');
      const count = document.getElementById(this.countId);
      if (live && count) live.textContent = count.textContent.trim();
    }

    swap(doc, id) {
      const next = doc.getElementById(id);
      const current = document.getElementById(id);
      if (next && current) current.innerHTML = next.innerHTML;
    }

    /* Re-render every filter group except the one the user is inside, so an
       open popover does not slam shut mid-interaction. */
    swapFilters(doc) {
      const active = document.activeElement;
      const activeGroup = active && active.closest ? active.closest('[data-v5-facet], [data-v5-facet-group]') : null;
      const activeId = activeGroup ? activeGroup.id : null;

      doc.querySelectorAll('[data-v5-facet], [data-v5-facet-group]').forEach((next) => {
        if (!next.id || next.id === activeId) return;
        const current = document.getElementById(next.id);
        if (!current) return;
        const wasOpen = current.open;
        current.innerHTML = next.innerHTML;
        if (current.tagName === 'DETAILS') current.open = wasOpen;
      });

      // Active-filter chips and the mobile count always refresh.
      ['V5ActiveFacets', 'V5ActiveFacetsMobile', 'V5FilterCount'].forEach((id) => this.swap(doc, id));
    }

    setBusy(state) {
      const grid = document.getElementById(this.gridId);
      if (grid) {
        grid.classList.toggle('is-busy', state);
        grid.setAttribute('aria-busy', String(state));
      }
    }
  }

  if (!customElements.get('v5-facets')) customElements.define('v5-facets', V5Facets);
})();
